/**
 * Jump navigation of the text sections (block-sections.php): the link of the
 * item being read carries .is-active — exactly one at a time.
 *
 * <nav data-sections-nav><a href="#abschnitt-…">…</a></nav>
 * <section class="sections-item" id="abschnitt-…">…</section>
 *
 * An item is being read once its top has crossed the nav line: the top of the
 * sticky aside, which is also the items' scroll-margin-top, i.e. where a jump
 * lands them. The block is a stacked section that freezes once its bottom
 * reaches the viewport bottom, so the last headings can never reach that
 * line. Their turns — and, so that each keeps about a wheel notch of scroll,
 * up to two headings before them — are squeezed proportionally into the
 * scroll left before the freeze; the last item is active exactly when the
 * section has been read to its end. A click on a link lands on that very
 * point (the module scrolls itself, header.js yields), so the item the
 * visitor chose is the item the rule reports.
 *
 * A chosen item (click, or the #hash the page was opened with) is marked at
 * once and held until the visitor scrolls away themselves — otherwise the spy
 * would flip through the neighbours while the animated scroll is travelling.
 */
import { claimed, flowTop, freezeScroll, isScrollKey, scrollToY } from './scroll'

const BAND = 100 // px of scroll a squeezed item keeps at least — about one wheel notch

const hashId = (href) => {
  const url = new URL(href, window.location.href)
  return url.hash.length > 1 ? decodeURIComponent(url.hash.slice(1)) : ''
}

const setup = (nav) => {
  const entries = [...nav.querySelectorAll('a[href*="#"]')]
    .map((link) => ({ link, item: document.getElementById(hashId(link.href)) }))
    .filter(({ item }) => item)
  if (!entries.length) return
  entries.forEach(({ item }) => claimed.add(item))

  const section = nav.closest('[data-section]')
  const last = entries.length - 1
  let active = null
  let held = null
  let settledY = null
  let ticking = false

  const mark = (entry) => {
    if (entry === active) return
    active = entry
    entries.forEach(({ link }) => {
      const on = entry.link === link
      link.classList.toggle('is-active', on)
      if (on) link.setAttribute('aria-current', 'location')
      else link.removeAttribute('aria-current')
    })
  }

  // Scroll position at which each item takes over — and where a jump to it lands.
  const points = () => {
    const margin = parseFloat(getComputedStyle(entries[0].item).scrollMarginTop) || 0
    const result = entries.map(({ item }) => flowTop(item) - margin)
    const freeze = freezeScroll(section)
    if (result[last] <= freeze) return result

    // Items beyond the freeze share the scroll that is left, in proportion.
    // The window opens at an earlier heading while they would get less than
    // a band each; a block that fits the viewport keeps its first item.
    let reachable = -1
    result.forEach((point, index) => {
      if (point <= freeze) reachable = index
    })
    if (reachable < 0) return result
    let start = reachable
    while (start > 0 && start > reachable - 2 && freeze - result[start] < BAND * (last - start)) {
      start -= 1
    }
    const scale = (freeze - result[start]) / (result[last] - result[start])
    for (let index = start + 1; index <= last; index += 1) {
      result[index] = result[start] + (result[index] - result[start]) * scale
    }
    return result
  }

  const spy = () => {
    ticking = false
    if (held) return
    const y = window.scrollY + 1
    let current = 0
    points().forEach((point, index) => {
      if (point <= y) current = index
    })
    mark(entries[current])
  }

  const request = () => {
    if (!ticking) {
      ticking = true
      window.requestAnimationFrame(spy)
    }
  }

  // Chosen item -----------------------------------------------------------
  const release = () => {
    if (!held) return
    held = null
    settledY = null
    request()
  }

  const go = async (entry, options) => {
    held = entry
    settledY = null
    mark(entry)
    const arrived = await scrollToY(
      Math.max(0, Math.round(points()[entries.indexOf(entry)])),
      options
    )
    if (held !== entry) return
    if (!arrived) {
      release()
      return
    }
    settledY = window.scrollY
    // The keyboard continues from the item, not from the navigation
    if (!entry.item.hasAttribute('tabindex')) entry.item.setAttribute('tabindex', '-1')
    entry.item.focus({ preventScroll: true })
  }

  entries.forEach((entry) =>
    entry.link.addEventListener('click', (event) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }
      event.preventDefault()
      window.history.pushState(null, '', `#${entry.item.id}`)
      go(entry)
    })
  )

  window.addEventListener(
    'scroll',
    () => {
      if (!held) request()
      else if (settledY !== null && Math.abs(window.scrollY - settledY) > 2) release()
    },
    { passive: true }
  )
  window.addEventListener('wheel', release, { passive: true })
  window.addEventListener('touchstart', release, { passive: true })
  window.addEventListener('keydown', (event) => {
    if (isScrollKey(event)) release()
  })
  window.addEventListener('resize', request)
  window.addEventListener('load', request)
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) release()
  })

  // The #hash the page was opened with (again after load: fonts move things),
  // or one typed into the address bar
  const jumpToHash = (options) => {
    const id = hashId(window.location.href)
    const entry = entries.find(({ item }) => item.id === id)
    if (entry) go(entry, options)
    return Boolean(entry)
  }
  window.addEventListener('hashchange', () => jumpToHash())
  const fresh = performance.getEntriesByType('navigation')[0]?.type !== 'back_forward'
  if (fresh && jumpToHash({ instant: true })) {
    window.addEventListener('load', () => jumpToHash({ instant: true }))
  } else {
    request()
  }
}

document.querySelectorAll('[data-sections-nav]').forEach(setup)
